import { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";

export default function Login() {
  const { user, login, bootstrapped } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  if (bootstrapped && user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      const target = loc.state?.from?.pathname || (res.user.role === "admin" ? "/admin" : "/");
      navigate(target, { replace: true });
    } else {
      setError(typeof res.error === "string" ? res.error : "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-hh-bg flex items-stretch" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between flex-1 px-16 py-14 text-white relative overflow-hidden"
           style={{ background: "linear-gradient(155deg, #1A1530 0%, #2708A1 65%, #0822D2 100%)" }}>
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(216,155,0,0.25), transparent 70%)" }} />
        <div className="absolute -left-24 bottom-0 h-72 w-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(181,25,222,0.15), transparent 70%)" }} />
        <div className="flex items-center gap-3 relative">
          <span className="h-12 w-12 rounded-2xl bg-white/95 backdrop-blur-sm p-1.5 grid place-items-center shadow-sm">
            <img src="/healing-house-logo.png" alt="Healing House Publishing" className="h-full w-full object-contain" />
          </span>
          <div className="font-serif text-xl">Healing House</div>
        </div>
        <div className="relative">
          <p className="hh-overline mb-6 text-white/60">The Author Portal</p>
          <h1 className="font-serif text-5xl xl:text-6xl leading-[1.05] tracking-tight">
            Your book&rsquo;s commercial life,<br/>
            <span className="italic text-[#FFB800]">honestly told.</span>
          </h1>
          <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-md">
            Every sale, every region, every royalty cent — laid out clearly so you can keep writing the next thing.
          </p>
        </div>
        <div className="text-xs text-white/50 relative">© {new Date().getFullYear()} Healing House Publishing</div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10 flex items-center gap-2.5">
            <img src="/healing-house-logo.png" alt="Healing House Publishing" className="h-11 w-11 object-contain" />
            <div className="font-serif text-lg text-hh-ink">Healing House</div>
          </div>
          <p className="hh-overline mb-3">Welcome back</p>
          <h2 className="font-serif text-3xl text-hh-ink mb-8">Sign in to your portal</h2>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm text-hh-ink2 mb-2" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
                className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40 focus:border-hh-primary transition-all"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm text-hh-ink2 mb-2" htmlFor="password">Password</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
                className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40 focus:border-hh-primary transition-all"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-hh-danger bg-[#FBEAE6] border border-[#F1CEC5] rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="hh-btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Sign in
            </button>

            <div className="text-center">
              <Link to="/forgot" data-testid="forgot-password-link" className="text-sm text-hh-primary hover:underline">
                Forgot your password?
              </Link>
            </div>
          </form>

          <p className="mt-10 text-xs text-hh-ink2 leading-relaxed">
            Trouble signing in? Email your publisher at{" "}
            <a className="text-hh-primary hover:underline" href="mailto:support@thebookdoula.com.au">support@thebookdoula.com.au</a>{" "}
            and we&rsquo;ll sort you out today.
          </p>
        </div>
      </div>
    </div>
  );
}
